import React from 'react';
import { ThumbsUpIcon, MessageCircleIcon, Share2Icon, MoreHorizontalIcon, GlobeIcon } from 'lucide-react';
// ─── Types ────────────────────────────────────────────────────────────────────
interface Comment {
  avatarInitials: string;
  avatarColor: string;
  avatarPhoto?: string;
  name: string;
  location: string;
  text: string;
  timeAgo: string;
  likes: number;
}
// ─── Avatar (initials, used for comments) ────────────────────────────────────
function Avatar({
  initials,
  color,
  size = 40
}: {
  initials: string;
  color: string;
  size?: number;
}) {
  return <div style={{
    width: size,
    height: size,
    borderRadius: '50%',
    background: color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: size < 36 ? 11 : 14,
    fontWeight: 700,
    color: '#fff',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    letterSpacing: '0.02em'
  }} data-id="element-486">
      {initials}
    </div>;
}
// ─── Brand Avatar (photo, used for post author) ───────────────────────────────
function BrandAvatar() {
  return <div style={{
    position: 'relative',
    flexShrink: 0
  }} data-id="element-487">
      <div style={{
      width: 42,
      height: 42,
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #C8A96E 0%, #A8893A 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      fontSize: 14,
      fontWeight: 700,
      color: '#fff',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      letterSpacing: '0.02em'
    }} data-id="element-488">
        DL
      </div>
    </div>;
}
// ─── Action Button ────────────────────────────────────────────────────────────
function ActionButton({
  icon,
  label
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return <button style={{
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '8px 4px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 4,
    color: '#65676B',
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'system-ui, -apple-system, sans-serif',
    transition: 'background 0.15s'
  }} onMouseEnter={e => e.currentTarget.style.background = '#F2F2F2'} onMouseLeave={e => e.currentTarget.style.background = 'none'} data-id="element-489">
      {icon}
      {label}
    </button>;
}
// ─── Comment Item ─────────────────────────────────────────────────────────────
function CommentItem({
  comment
}: {
  comment: Comment;
}) {
  return <div style={{
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8
  }} data-id="element-490">
      {comment.avatarPhoto ? <img src={comment.avatarPhoto} alt={comment.name} style={{
      width: 36,
      height: 36,
      borderRadius: '50%',
      objectFit: 'cover',
      display: 'block',
      flexShrink: 0
    }} data-id="element-491" /> : <Avatar initials={comment.avatarInitials} color={comment.avatarColor} size={36} data-id="element-492" />}
      <div style={{
      flex: 1
    }} data-id="element-493">
        <div style={{
        background: '#F0F2F5',
        borderRadius: 18,
        padding: '10px 14px',
        display: 'inline-block',
        maxWidth: '100%'
      }} data-id="element-494">
          <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 2
        }} data-id="element-495">
            <span style={{
            fontWeight: 700,
            fontSize: 13,
            color: '#050505'
          }} data-id="element-496">
              {comment.name}
            </span>
          </div>
          <span style={{
          fontSize: 14,
          color: '#050505',
          lineHeight: 1.5,
          display: 'block'
        }} data-id="element-497">
            {comment.text}
          </span>
        </div>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginTop: 4,
        paddingLeft: 12
      }} data-id="element-498">
          <span style={{
          fontSize: 11,
          color: '#65676B',
          fontWeight: 600,
          cursor: 'pointer'
        }} data-id="element-499">
            Me gusta
          </span>
          <span style={{
          fontSize: 11,
          color: '#65676B',
          fontWeight: 600,
          cursor: 'pointer'
        }} data-id="element-500">
            Responder
          </span>
          <span style={{
          fontSize: 11,
          color: '#65676B'
        }} data-id="element-501">
            {comment.timeAgo}
          </span>
          {comment.likes > 0 && <span style={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          marginLeft: 'auto'
        }} data-id="element-502">
              <span style={{
            fontSize: 13
          }} data-id="element-503">
                👍
              </span>
              <span style={{
            fontSize: 12,
            color: '#65676B'
          }} data-id="element-504">
                {comment.likes}
              </span>
            </span>}
        </div>
      </div>
    </div>;
}
// ─── Comments Data ────────────────────────────────────────────────────────────
const comments: Comment[] = [{
  avatarInitials: 'MG',
  avatarColor: '#E91E63',
  avatarPhoto: 'https://cdn.magicpatterns.com/uploads/pdeJsFumYyyRvxaPNepWNc/33.jpg',
  name: 'Marcela Gomez',
  location: 'Colombia',
  text: 'Hice el Pastel de Tres Leches para el cumpleaños de mi mamá de 70 años. Cuando lo probó cerró los ojos y dijo: «Mija, esto sabe igual al que hacía tu abuela.» Me puse a llorar. Por primera vez en 6 años con celiaquía no me sentí diferente en una celebración familiar. 😭❤️',
  timeAgo: '3 de enero',
  likes: 234
}, {
  avatarInitials: 'VR',
  avatarColor: '#2196F3',
  avatarPhoto: 'https://cdn.magicpatterns.com/uploads/gRXAPvZoJhoQmn8WE6KPQB/45.jpg',
  name: 'Valentina R.',
  location: '',
  text: 'Llevo 3 años buscando una receta de alfajores sin gluten que no se deshaga. Los encontré en este libro. Mi marido, que no tiene ninguna restricción, me pide que los haga todas las semanas. 🍪😂',
  timeAgo: '11 de enero',
  likes: 189
}, {
  avatarInitials: 'CM',
  avatarColor: '#FF9800',
  avatarPhoto: 'https://cdn.magicpatterns.com/uploads/mfcyf6oigpUatHeSr2RHQj/63.jpg',
  name: 'Carolina Moncayo',
  location: '',
  text: 'El Brownie Fudge lo hice para una reunión de trabajo sin decirle a nadie que era sin gluten ni sin azúcar. Se terminó en 10 minutos y dos compañeras me pidieron la receta. Ese momento no tiene precio. 🍫🙌',
  timeAgo: '18 de enero',
  likes: 312
}, {
  avatarInitials: 'PT',
  avatarColor: '#673AB7',
  avatarPhoto: 'https://cdn.magicpatterns.com/uploads/uw9aWkrhcUEVbipFQZBGJX/14.jpg',
  name: 'Paola T.',
  location: '',
  text: 'Lo que más me cambió no fue una receta específica. Fue el Capítulo 3, «Los 5 Secretos de la Textura». Después de leerlo entendí por qué TODO lo que había hecho antes quedaba mal. Es el capítulo que debería estar en todos los libros de cocina sin gluten. ✨',
  timeAgo: '25 de enero',
  likes: 156
}, {
  avatarInitials: 'RS',
  avatarColor: '#00BCD4',
  avatarPhoto: 'https://cdn.magicpatterns.com/uploads/5aTCRoFGBceHTWu3Hf45MK/8.jpg',
  name: 'Carla Castillo',
  location: '',
  text: 'Soy diabética tipo 2 y mi médico me prohibió el azúcar hace 2 años. Este libro me devolvió el placer de comer postre en familia. El Flan de Vainilla es exactamente igual al que hacía mi abuela cuando era pequeña! No lo puedo creer. 🙏❤️',
  timeAgo: '2 de febrero',
  likes: 278
}];
// ─── Main Section ─────────────────────────────────────────────────────────────
export function TestimonialsSection() {
  return <section className="w-full py-20 px-6 pt-[30px] pb-[30px]" style={{
    background: 'var(--cream-dark)'
  }} aria-label="Testimonios" data-id="element-505">
      <div className="max-w-6xl mx-auto" data-id="element-506">
        {/* Section Header */}
        <div className="text-center mb-14" data-id="element-507">
          <p className="text-sm font-bold tracking-widest uppercase mb-4" style={{
          color: 'var(--gold-dark)'
        }} data-id="element-508">
            Resultados Reales
          </p>
          <h2 className="text-4xl md:text-5xl font-bold" style={{
          color: 'var(--brown)'
        }} data-id="element-509">
            Lo Que Dicen Quienes Ya Lo Tienen
          </h2>
        </div>

        {/* Single Facebook Post */}
        <div style={{
        display: 'flex',
        justifyContent: 'center'
      }} data-id="element-510">
          <div style={{
          background: '#FFFFFF',
          borderRadius: 8,
          boxShadow: '0 1px 2px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.05)',
          maxWidth: 500,
          width: '100%',
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          overflow: 'hidden'
        }} data-id="element-511">
            {/* ── Post Header ── */}
            <div style={{
            padding: '12px 16px 0',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }} data-id="element-512">
              <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10
            }} data-id="element-513">
                <BrandAvatar data-id="element-514" />
                <div data-id="element-515">
                  <div style={{
                  fontWeight: 700,
                  fontSize: 15,
                  color: '#050505',
                  lineHeight: 1.3
                }} data-id="element-516">
                    Dulces Sin Límites
                  </div>
                  <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  marginTop: 1
                }} data-id="element-517">
                    <span style={{
                    fontSize: 12,
                    color: '#65676B'
                  }} data-id="element-518">
                      5 de febrero
                    </span>
                    <span style={{
                    fontSize: 12,
                    color: '#65676B'
                  }} data-id="element-519">
                      ·
                    </span>
                    <GlobeIcon size={12} color="#65676B" data-id="element-520" />
                  </div>
                </div>
              </div>
              <button style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: 4,
              color: '#65676B',
              display: 'flex',
              alignItems: 'center'
            }} aria-label="Más opciones" data-id="element-521">
                <MoreHorizontalIcon size={20} data-id="element-522" />
              </button>
            </div>

            {/* ── Post Body Text ── */}
            <div style={{
            padding: '10px 16px 12px',
            fontSize: 15,
            color: '#050505',
            lineHeight: 1.6
          }} data-id="element-523">
              ¿Quién ya descargó la guía? 🙌 Cuéntanos cómo te fue! Queremos
              leer sus historias 💕
            </div>

            {/* ── Post Image (ebook cover) ── */}
            <img src="https://cdn.magicpatterns.com/uploads/1xeyM8XY99HJAJT6xgmREj/heroprod.jpg" alt="Dulce Libertad — Ebook de repostería sin gluten, sin lácteos y sin azúcar" style={{
            width: '100%',
            display: 'block',
            objectFit: 'cover'
          }} data-id="element-524" />

            {/* ── Reactions Bar ── */}
            <div style={{
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }} data-id="element-525">
              <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }} data-id="element-526">
                <div style={{
                display: 'flex',
                alignItems: 'center'
              }} data-id="element-527">
                  <span style={{
                  fontSize: 16,
                  lineHeight: 1
                }} data-id="element-528">
                    👍
                  </span>
                  <span style={{
                  fontSize: 16,
                  lineHeight: 1,
                  marginLeft: -4
                }} data-id="element-529">
                    ❤️
                  </span>
                  <span style={{
                  fontSize: 16,
                  lineHeight: 1,
                  marginLeft: -4
                }} data-id="element-530">
                    😍
                  </span>
                </div>
                <span style={{
                fontSize: 13,
                color: '#65676B',
                marginLeft: 4
              }} data-id="element-531">
                  1.2 mil
                </span>
              </div>
              <span style={{
              fontSize: 13,
              color: '#65676B'
            }} data-id="element-532">
                {comments.length} comentarios
              </span>
            </div>

            {/* ── Divider ── */}
            <div style={{
            height: 1,
            background: '#E4E6EB',
            margin: '0 16px'
          }} data-id="element-533" />

            {/* ── Action Buttons ── */}
            <div style={{
            display: 'flex',
            padding: '2px 8px'
          }} data-id="element-534">
              <ActionButton icon={<ThumbsUpIcon size={18} data-id="element-536" />} label="Me gusta" data-id="element-535" />
              <ActionButton icon={<MessageCircleIcon size={18} data-id="element-538" />} label="Comentar" data-id="element-537" />
              <ActionButton icon={<Share2Icon size={18} data-id="element-540" />} label="Compartir" data-id="element-539" />
            </div>

            {/* ── Divider ── */}
            <div style={{
            height: 1,
            background: '#E4E6EB',
            margin: '0 16px'
          }} data-id="element-541" />

            {/* ── Comments ── */}
            <div style={{
            padding: '12px 16px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 14
          }} data-id="element-542">
              {comments.map((comment, i) => <CommentItem key={i} comment={comment} data-id="element-543" />)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}